class NtjMenu {
  constructor(rail) {
    this.rail = rail;
    this.details = rail.querySelector('[data-ntj-navigation]');
    this.button = rail.querySelector('[data-ntj-menu-button]');
    this.panel = rail.querySelector('[data-ntj-menu-panel]');
    this.backdrop = rail.querySelector('[data-ntj-menu-backdrop]');
    this.closeButton = rail.querySelector('[data-ntj-menu-close]');
    this.label = rail.querySelector('[data-ntj-menu-label]');
    this.main = rail.closest('[data-ntj-storefront]')?.querySelector('.ntj-storefront__main') || null;
    this.mainWasInert = Boolean(this.main?.inert);

    if (!this.details || !this.button || !this.panel || rail.dataset.ntjMenuReady === 'true') {
      return;
    }

    rail.dataset.ntjMenuReady = 'true';
    this.media = window.matchMedia('(max-width: 1024px)');
    this.details.addEventListener('toggle', () => this.syncState());
    this.backdrop?.addEventListener('click', () => this.close(true));
    this.closeButton?.addEventListener('click', () => this.close(true));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.panel.addEventListener('click', (event) => {
      if (event.target.closest('a') && this.media.matches) {
        this.close();
      }
    });
    this.media.addEventListener('change', () => this.syncViewport());
    this.syncViewport();
  }

  syncViewport() {
    if (this.media.matches) {
      this.close();
      return;
    }

    this.details.open = true;
    this.syncState();
  }

  toggle() {
    if (!this.media.matches) {
      return;
    }

    if (this.details.open) {
      this.close(true);
      return;
    }

    this.open();
  }

  open() {
    this.details.open = true;
  }

  close(restoreFocus = false) {
    this.details.open = false;

    if (restoreFocus) {
      this.button.focus();
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.media.matches || !this.details.open) {
      return;
    }

    event.preventDefault();
    this.close(true);
  }

  syncState() {
    const expanded = this.media.matches && this.details.open;
    this.updateLabel(expanded);
    document.body.classList.toggle('ntj-menu-open', expanded);
    if (this.main) this.main.inert = expanded ? true : this.mainWasInert;
    if (this.backdrop) this.backdrop.hidden = !expanded;
    if (expanded) this.closeButton?.focus();
  }

  updateLabel(expanded) {
    if (this.label) {
      this.label.textContent = expanded ? 'Close navigation' : 'Open navigation';
    }
  }
}

class NtjMotion {
  constructor(storefront) {
    this.storefront = storefront;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.reducedMotion.matches || storefront.dataset.ntjMotionReady === 'true') {
      return;
    }

    const items = [...storefront.querySelectorAll('[data-ntj-reveal]')];
    if (!items.length) {
      return;
    }

    storefront.dataset.ntjMotionReady = 'true';
    storefront.classList.add('ntj-motion-ready');
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => this.observer.observe(item));
  }
}

class NtjProductGallery {
  constructor(gallery) {
    if (gallery.dataset.ntjGalleryReady === 'true') return;

    this.gallery = gallery;
    this.triggers = [...gallery.querySelectorAll('[data-ntj-gallery-trigger]')];
    this.panels = [...gallery.querySelectorAll('[data-ntj-gallery-panel]')];
    this.count = gallery.querySelector('[data-ntj-gallery-count]');
    this.previous = gallery.querySelector('[data-ntj-gallery-previous]');
    this.next = gallery.querySelector('[data-ntj-gallery-next]');
    this.index = 0;

    if (!this.triggers.length || this.triggers.length !== this.panels.length) return;

    gallery.dataset.ntjGalleryReady = 'true';
    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.show(index));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') this.focus(index + 1, event);
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') this.focus(index - 1, event);
      });
    });
    this.previous?.addEventListener('click', () => this.show(this.index - 1));
    this.next?.addEventListener('click', () => this.show(this.index + 1));
    this.show(0);
  }

  focus(index, event) {
    event.preventDefault();
    const next = (index + this.triggers.length) % this.triggers.length;
    this.triggers[next].focus();
    this.show(next);
  }

  show(index) {
    this.index = (index + this.panels.length) % this.panels.length;
    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === this.index;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      this.triggers[panelIndex].classList.toggle('is-active', active);
      this.triggers[panelIndex].setAttribute('aria-selected', String(active));
    });
    if (this.count) this.count.textContent = `${this.index + 1} of ${this.panels.length}`;
  }
}

const initializeNtjStorefront = (root = document) => {
  root.querySelectorAll('[data-ntj-rail]').forEach((rail) => new NtjMenu(rail));
  root.querySelectorAll('[data-ntj-storefront]').forEach((storefront) => new NtjMotion(storefront));
  root.querySelectorAll('[data-ntj-product-gallery]').forEach((gallery) => new NtjProductGallery(gallery));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeNtjStorefront(), { once: true });
} else {
  initializeNtjStorefront();
}

document.addEventListener('shopify:section:load', (event) => initializeNtjStorefront(event.target));

